export const MSI_CLEAN_THEME_ID = 'msiClean';
export const MSI_CLEAN_THEME_LABEL = 'MSI Clean';

export const MSI_PALETTE = Object.freeze({
  canvas: '#eef2f6',
  navy950: '#0c1726',
  navy900: '#132238',
  navy800: '#1c314d',
  navy700: '#294a6e',
  blue600: '#2364aa',
  blue100: '#dcebf8',
  ink900: '#182230',
  ink700: '#344054',
  ink500: '#667085',
  ink400: '#98a2b3',
  line: '#d8e0e8',
  lineDark: '#c7d1dc',
  surface: '#ffffff',
  surfaceSubtle: '#f7f9fb',
  success: '#087a55',
  successBg: '#e8f7f0',
  warning: '#9a5b08',
  warningBg: '#fff4d8',
  danger: '#b42318',
  dangerBg: '#feeeee',
  info: '#175cd3',
  infoBg: '#edf4ff',
  event: '#5b34b0',
  eventBg: '#f1ecfb',
  chromeDivider: '#26364a',
  navInactive: '#aebed0',
  navSecondary: '#91a4ba',
  navHover: '#17293f',
  navActiveFill: '#1d3856',
  navActiveBorder: '#3e5d7d',
  brandMarkFill: '#1b3553',
  brandMarkBorder: '#4f6f91',
  brandMarkText: '#d6e9fa',
});

export const MSI_COMPONENT_COLORS = Object.freeze({
  tableHeader: '#f5f7fa',
  rowDivider: '#e8edf2',
  rowHover: '#f9fbfd',
  selectedRow: '#edf5fc',
  selectedAccent: '#3d78af',
  focusBorder: '#5488bb',
  focusRing: '#dceaf7',
  primaryBorder: '#1c5c9b',
  primaryHover: '#1b5590',
  secondaryHover: '#f4f7fa',
  secondaryHoverBorder: '#99afc5',
  dangerAction: '#c63d32',
  dangerActionBorder: '#c7392d',
  disabledBorder: '#d7dee6',
  disabledFill: '#e9edf2',
  disabledText: '#8e9baa',
  semanticBorders: Object.freeze({
    success: '#9ed9c2',
    warning: '#e8c77e',
    danger: '#f0a8a2',
    info: '#aac7ef',
    event: '#c9b8ef',
  }),
});

export const MSI_FONT_FAMILY = 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export const MSI_TYPE_SCALE = Object.freeze({
  pageTitle: Object.freeze({ fontSize: '20px', fontWeight: 700, lineHeight: 1.15, letterSpacing: 0 }),
  panelTitle: Object.freeze({ fontSize: '15px', fontWeight: 700, letterSpacing: 0 }),
  navigationLabel: Object.freeze({ fontSize: '12.5px', fontWeight: 650, letterSpacing: 0 }),
  body: Object.freeze({ fontSize: '12px', letterSpacing: 0 }),
  helper: Object.freeze({ fontSize: '11px', letterSpacing: 0 }),
  tableBody: Object.freeze({ fontSize: '12.5px', fontWeight: 500, lineHeight: 1.35, letterSpacing: 0 }),
  tableHeader: Object.freeze({ fontSize: '11px', fontWeight: 800, lineHeight: 1.2, letterSpacing: 0, textTransform: 'uppercase' }),
  statusBadge: Object.freeze({ fontSize: '9px', fontWeight: 800, letterSpacing: 0, textTransform: 'uppercase' }),
});

export const MSI_GEOMETRY = Object.freeze({
  radii: Object.freeze({
    base: 7,
    panel: 8,
    navigation: 7,
    input: 7,
    button: 7,
    iconButton: 6,
    statusBadge: 999,
  }),
  borderWidth: 1,
  controlHeight: 36,
  compactControlHeight: 30,
  tableHeaderHeight: 34,
  tableRowHeight: 48,
  cardBoxShadow: 'none',
});

export const MSI_CHROME_TOKENS = Object.freeze({
  background: MSI_PALETTE.navy950,
  controlBackground: MSI_PALETTE.navy900,
  hover: MSI_PALETTE.navHover,
  activeFill: MSI_PALETTE.navActiveFill,
  activeBorder: MSI_PALETTE.navActiveBorder,
  primaryText: MSI_PALETTE.surface,
  secondaryText: MSI_PALETTE.navInactive,
  mutedText: MSI_PALETTE.navSecondary,
  divider: MSI_PALETTE.chromeDivider,
});

export const MSI_SEMANTIC_TOKENS = Object.freeze({
  success: Object.freeze({
    foreground: MSI_PALETTE.success,
    background: MSI_PALETTE.successBg,
    border: MSI_COMPONENT_COLORS.semanticBorders.success,
  }),
  warning: Object.freeze({
    foreground: MSI_PALETTE.warning,
    background: MSI_PALETTE.warningBg,
    border: MSI_COMPONENT_COLORS.semanticBorders.warning,
  }),
  danger: Object.freeze({
    foreground: MSI_PALETTE.danger,
    background: MSI_PALETTE.dangerBg,
    border: MSI_COMPONENT_COLORS.semanticBorders.danger,
  }),
  info: Object.freeze({
    foreground: MSI_PALETTE.info,
    background: MSI_PALETTE.infoBg,
    border: MSI_COMPONENT_COLORS.semanticBorders.info,
  }),
  actual: MSI_PALETTE.blue600,
  optimized: '#a5d6a7',
  ai: '#90caf9',
  positive: MSI_PALETTE.success,
  negative: MSI_PALETTE.danger,
});

export const MSI_MUI_TOKENS = Object.freeze({
  palette: MSI_PALETTE,
  componentColors: MSI_COMPONENT_COLORS,
  chrome: MSI_CHROME_TOKENS,
  semantics: MSI_SEMANTIC_TOKENS,
  actions: Object.freeze({
    primaryText: MSI_PALETTE.surface,
    dangerText: MSI_PALETTE.surface,
  }),
  typography: Object.freeze({
    fontFamily: MSI_FONT_FAMILY,
    scale: MSI_TYPE_SCALE,
  }),
  geometry: MSI_GEOMETRY,
});

const clampChannel = (value) => Math.max(0, Math.min(255, Math.round(value)));

const parseHex = (hex) => {
  const raw = String(hex || '').replace('#', '');
  const full = raw.length === 3 ? raw.split('').map((channel) => channel + channel).join('') : raw;
  const value = Number.parseInt(full, 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

const mixHex = (foreground, background, amount) => {
  const from = parseHex(background);
  const to = parseHex(foreground);
  const ratio = Math.max(0, Math.min(1, amount));
  const channel = (key) => clampChannel(from[key] + ((to[key] - from[key]) * ratio));
  return `#${[channel('r'), channel('g'), channel('b')]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')}`;
};

const relativeLuminance = (color) => {
  const channels = Object.values(parseHex(color)).map((value) => {
    const normalized = value / 255;
    return normalized <= 0.04045
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return (0.2126 * channels[0]) + (0.7152 * channels[1]) + (0.0722 * channels[2]);
};

const contrastRatio = (foreground, background) => {
  const foregroundLuminance = relativeLuminance(foreground);
  const backgroundLuminance = relativeLuminance(background);
  const lighter = Math.max(foregroundLuminance, backgroundLuminance);
  const darker = Math.min(foregroundLuminance, backgroundLuminance);
  return (lighter + 0.05) / (darker + 0.05);
};

const contrastText = (background) => {
  const blackContrast = contrastRatio('#000000', background);
  const whiteContrast = contrastRatio('#ffffff', background);
  return blackContrast >= whiteContrast ? '#000000' : '#ffffff';
};

const readableForeground = (foreground, background, minimumContrast = 4.5) => {
  if (contrastRatio(foreground, background) >= minimumContrast) return foreground;

  const target = contrastText(background);
  for (let step = 1; step <= 20; step += 1) {
    const candidate = mixHex(target, foreground, step / 20);
    if (contrastRatio(candidate, background) >= minimumContrast) return candidate;
  }
  return target;
};

const hasPinnedMsiColors = (theme) => {
  const colors = theme?.colors || {};
  return theme?.id === MSI_CLEAN_THEME_ID
    && colors.bgBase === MSI_PALETTE.canvas
    && colors.surface === MSI_PALETTE.surface
    && colors.elevated === MSI_PALETTE.surfaceSubtle
    && colors.deep === MSI_COMPONENT_COLORS.tableHeader
    && colors.accent === MSI_PALETTE.blue600
    && colors.border === MSI_PALETTE.line
    && colors.textPrimary === MSI_PALETTE.ink900
    && colors.textSecondary === MSI_PALETTE.ink500
    && colors.positive === MSI_PALETTE.success
    && colors.negative === MSI_PALETTE.danger
    && colors.actual === MSI_PALETTE.blue600;
};

/**
 * Builds the one universal MSI-derived color-role contract consumed by every
 * registered Alphamizer theme. Geometry and typography remain the frozen MSI
 * objects; only these color roles vary by palette.
 */
export function buildAppStyleTokens(theme) {
  // Preserve the exact reference object for the default MSI Clean palette.
  // A customized MSI Clean palette must still derive live semantic roles.
  if (hasPinnedMsiColors(theme)) return MSI_MUI_TOKENS;

  const colors = theme?.colors || {};
  const dark = theme?.mode === 'dark';
  const highContrast = theme?.contrast === 'high';
  const surface = colors.surface;
  const elevated = colors.elevated;
  const border = colors.border;
  const primaryText = colors.textPrimary;
  const secondaryText = colors.textSecondary;
  const accent = colors.accent;
  const fillAmount = highContrast ? 0.2 : (dark ? 0.22 : 0.12);
  const borderAmount = highContrast ? 0.68 : 0.46;
  const warningBase = dark ? '#f0b45c' : '#8a570a';
  const successBg = mixHex(colors.positive, surface, fillAmount);
  const warningBg = mixHex(warningBase, surface, fillAmount);
  const dangerBg = mixHex(colors.negative, surface, fillAmount);
  const infoBg = mixHex(accent, surface, fillAmount);
  // Your own Compare & Explore events: violet, which no portfolio line and no accent uses.
  const eventBase = dark ? '#b79cff' : '#6a3dc4';
  const eventBg = mixHex(eventBase, surface, fillAmount);
  const success = readableForeground(colors.positive, successBg);
  const warning = readableForeground(warningBase, warningBg);
  const danger = readableForeground(colors.negative, dangerBg);
  const info = readableForeground(accent, infoBg);
  const event = readableForeground(eventBase, eventBg);
  const chromeSecondaryText = readableForeground(secondaryText, colors.deep);

  const palette = Object.freeze({
    canvas: colors.bgBase,
    navy950: colors.deep,
    navy900: surface,
    navy800: elevated,
    navy700: border,
    blue600: accent,
    blue100: mixHex(accent, surface, dark ? 0.24 : 0.13),
    ink900: primaryText,
    ink700: primaryText,
    ink500: secondaryText,
    ink400: mixHex(secondaryText, surface, dark ? 0.72 : 0.68),
    line: border,
    lineDark: highContrast ? border : mixHex(primaryText, border, dark ? 0.18 : 0.12),
    surface,
    surfaceSubtle: elevated,
    success,
    successBg,
    warning,
    warningBg,
    danger,
    dangerBg,
    info,
    infoBg,
    event,
    eventBg,
    chromeDivider: border,
    navInactive: chromeSecondaryText,
    navSecondary: chromeSecondaryText,
    navHover: elevated,
    navActiveFill: mixHex(accent, colors.deep, dark ? 0.34 : 0.18),
    navActiveBorder: highContrast ? border : mixHex(accent, border, 0.58),
    brandMarkFill: elevated,
    brandMarkBorder: border,
    brandMarkText: primaryText,
  });

  const componentColors = Object.freeze({
    tableHeader: colors.deep,
    rowDivider: highContrast ? border : mixHex(border, surface, dark ? 0.72 : 0.58),
    rowHover: mixHex(primaryText, surface, dark ? 0.08 : 0.035),
    selectedRow: mixHex(accent, surface, dark ? 0.24 : 0.1),
    selectedAccent: accent,
    focusBorder: accent,
    focusRing: mixHex(accent, surface, dark ? 0.36 : 0.2),
    primaryBorder: highContrast ? border : mixHex(primaryText, accent, 0.18),
    primaryHover: mixHex(primaryText, accent, dark ? 0.14 : 0.1),
    secondaryHover: mixHex(primaryText, surface, dark ? 0.08 : 0.04),
    secondaryHoverBorder: highContrast ? border : mixHex(primaryText, border, 0.24),
    dangerAction: colors.negative,
    dangerActionBorder: highContrast ? border : mixHex(primaryText, colors.negative, 0.16),
    disabledBorder: highContrast ? border : mixHex(border, surface, 0.62),
    disabledFill: mixHex(primaryText, surface, dark ? 0.1 : 0.08),
    disabledText: mixHex(secondaryText, surface, dark ? 0.7 : 0.62),
    semanticBorders: Object.freeze({
      success: mixHex(colors.positive, border, borderAmount),
      warning: mixHex(palette.warning, border, borderAmount),
      danger: mixHex(colors.negative, border, borderAmount),
      info: mixHex(accent, border, borderAmount),
      event: mixHex(eventBase, border, borderAmount),
    }),
  });

  return Object.freeze({
    palette,
    componentColors,
    chrome: Object.freeze({
      background: colors.deep,
      controlBackground: surface,
      hover: elevated,
      activeFill: palette.navActiveFill,
      activeBorder: palette.navActiveBorder,
      primaryText,
      secondaryText: chromeSecondaryText,
      mutedText: chromeSecondaryText,
      divider: border,
    }),
    semantics: Object.freeze({
      success: Object.freeze({ foreground: success, background: successBg, border: componentColors.semanticBorders.success }),
      warning: Object.freeze({ foreground: warning, background: warningBg, border: componentColors.semanticBorders.warning }),
      danger: Object.freeze({ foreground: danger, background: dangerBg, border: componentColors.semanticBorders.danger }),
      info: Object.freeze({ foreground: info, background: infoBg, border: componentColors.semanticBorders.info }),
      actual: colors.actual,
      optimized: colors.optimized,
      ai: colors.ai,
      positive: colors.positive,
      negative: colors.negative,
    }),
    actions: Object.freeze({
      primaryText: contrastText(accent),
      dangerText: contrastText(colors.negative),
    }),
    typography: Object.freeze({ fontFamily: MSI_FONT_FAMILY, scale: MSI_TYPE_SCALE }),
    geometry: MSI_GEOMETRY,
  });
}

export const getAppStyleTokens = (theme) => (
  theme?.palette?.appStyle || MSI_MUI_TOKENS
);

// Shared/page-level `sx` helpers use these CSS roles so the same style object
// follows live theme and user-color changes without theme-id conditionals.
export const APP_STYLE_PALETTE = Object.freeze(Object.fromEntries(
  Object.keys(MSI_PALETTE).map((role) => [role, `var(--app-${role})`]),
));

export const APP_STYLE_COMPONENT_COLORS = Object.freeze({
  ...Object.fromEntries(
    Object.keys(MSI_COMPONENT_COLORS)
      .filter((role) => role !== 'semanticBorders')
      .map((role) => [role, `var(--app-${role})`]),
  ),
  semanticBorders: Object.freeze(Object.fromEntries(
    Object.keys(MSI_COMPONENT_COLORS.semanticBorders)
      .map((role) => [role, `var(--app-semantic-border-${role})`]),
  )),
});

export const isMsiCleanTheme = (themeId) => themeId === MSI_CLEAN_THEME_ID;

export const usesAppStyleGrammar = (theme) => Boolean(
  theme?.palette?.appStyle
);

// Compatibility alias for external imports; application source uses the
// universal name above. It returns true for every registered app theme.
export const isMsiCleanMuiTheme = usesAppStyleGrammar;

// MUI's sx `borderRadius` transform multiplies numeric values by
// theme.shape.borderRadius. Registered app themes use literal MSI pixels;
// the fallback value supports isolated external/test themes without appStyle.
export const resolveMsiSxRadius = (theme, msiPixels, fallbackSxValue) => (
  usesAppStyleGrammar(theme) ? `${msiPixels}px` : fallbackSxValue
);
